import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';
import inclusionsService from '~/services/cruises/InclusionsService';

import { CruiseDealPage } from '../types';

const MAX_DEAL_INCLUSIONS_PREVIEW = 3;

interface Props {
  dealPage: CruiseDealPage;
}

function DealPageOfferInclusionCreation({ dealPage }: Props) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const dealInclusionsPreview = useMemo(
    () => (dealPage.inclusions || []).sort((a, b) => a.order - b.order).slice(0, MAX_DEAL_INCLUSIONS_PREVIEW),
    [dealPage.inclusions],
  );

  const hasFilters = dealPage.rates.length > 0 || dealPage.departures.length > 0;

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    handleCloseDialog();
    setIsCreating(true);

    try {
      const newInclusion = await inclusionsService.createFromDeal(dealPage.id);

      enqueueSnackbar('Offer inclusion created successfully', {
        variant: 'success',
        action: (snackbarId) => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(snackbarId);
              history.push(`/cruises/inclusions/edit/${newInclusion.id}/items`);
            }}
          >
            View Inclusion
          </Button>
        ),
      });
    } catch (error) {
      enqueueSnackbar(`Error creating offer inclusion: ${error}`, { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  }, [dealPage.id, handleCloseDialog, enqueueSnackbar, closeSnackbar, history]);

  return (
    <>
      <Button variant="contained" onClick={handleOpenDialog} disabled={isCreating}>
        {isCreating && <Spinner size={20} />}
        Create Offer Inclusion
      </Button>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Offer Inclusion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            A new offer inclusion will be created with the following data from this deal page:
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{dealPage.name}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body1">STANDARD</Typography>
            </Box>

            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body1">{formatDateLongMonthWithMeridiemNoSeconds(dealPage.startDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  End Date
                </Typography>
                <Typography variant="body1">{formatDateLongMonthWithMeridiemNoSeconds(dealPage.endDate)}</Typography>
              </Box>
            </Stack>

            {dealInclusionsPreview.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Inclusions (first {MAX_DEAL_INCLUSIONS_PREVIEW}, as free-text)
                  </Typography>
                  <Stack spacing={0.5} mt={0.5}>
                    {dealInclusionsPreview.map((item, index) => (
                      <Typography key={item.id} variant="body2">
                        {index + 1}. {item.description}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </>
            )}

            {hasFilters && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Filters
                  </Typography>
                  <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
                    {dealPage.rates.map((rate) => (
                      <Chip key={rate.id} label={`Rate: ${rate.code}`} size="small" variant="outlined" />
                    ))}
                    {dealPage.departures.map((departure) => (
                      <Chip key={departure.id} label={`Departure: ${departure.name}`} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DealPageOfferInclusionCreation;
